import React, { useState, useMemo, useEffect } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { Reveal, Stagger, StaggerItem } from "../components/Reveal";
import { PROJECTS, PROJECT_CATEGORIES } from "../lib/content";

export default function Projetos() {
  const { categoria } = useParams();
  const navigate = useNavigate();
  const initial = categoria && PROJECT_CATEGORIES.some((c) => c.slug === categoria) ? categoria : "todos";
  const [filter, setFilter] = useState(initial);

  useEffect(() => {
    setFilter(initial);
  }, [initial]);

  const projects = useMemo(() => {
    if (filter === "todos") return PROJECTS;
    return PROJECTS.filter((p) => p.category === filter);
  }, [filter]);

  return (
    <>
      <section className="pt-28 md:pt-36 pb-10 bg-white">
        <div className="sca-container">
          <Reveal>
            <p className="sca-overline mb-6">[ Portfólio ]</p>
            <h1 className="sca-h1 max-w-[20ch]">
              Projetos executados com <span className="sca-serif text-sca-red">controle, organização e responsabilidade</span>.
            </h1>
          </Reveal>

          {/* Filters */}
          <Reveal delay={0.15}>
            <div className="mt-12 md:mt-16 border-t border-sca-line pt-6 flex flex-wrap gap-x-8 gap-y-3">
              {PROJECT_CATEGORIES.map((c) => {
                const active = filter === c.slug;
                return (
                  <button
                    key={c.slug}
                    onClick={() => {
                      setFilter(c.slug);
                      navigate(c.slug === "todos" ? "/projetos" : `/projetos/${c.slug}`, { replace: true });
                    }}
                    data-testid={`projects-filter-${c.slug}`}
                    className={`relative pb-2 text-[12px] tracking-[0.22em] uppercase font-bold transition-colors ${
                      active ? "text-sca-red" : "text-sca-black hover:text-sca-red"
                    }`}
                  >
                    {c.label}
                    {active && (
                      <span className="absolute left-0 right-0 -bottom-px h-0.5 bg-sca-red" aria-hidden />
                    )}
                  </button>
                );
              })}
            </div>
          </Reveal>
        </div>
      </section>

      <section className="sca-section pt-12 bg-white">
        <div className="sca-container">
          <Stagger className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-16">
            {projects.map((p, i) => (
              <StaggerItem key={p.id} className={i % 2 === 1 ? "md:translate-y-16" : ""}>
                <article className="group" data-testid={`project-card-${p.id}`}>
                  <div className="aspect-[5/4] overflow-hidden bg-sca-mist">
                    <img
                      src={p.image}
                      alt={p.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-[1400ms]"
                      loading="lazy"
                    />
                  </div>
                  <div className="pt-6 flex items-start justify-between gap-6">
                    <div className="flex-1">
                      <p className="text-[11px] tracking-[0.24em] uppercase font-bold text-sca-red mb-2">
                        {p.year} · {p.location}
                      </p>
                      <h3 className="font-sans text-2xl md:text-3xl font-semibold leading-tight max-w-md tracking-tight">
                        {p.title}
                      </h3>
                      <p className="mt-3 text-sm md:text-base text-sca-steel leading-relaxed max-w-md">{p.summary}</p>
                      <div className="mt-5 flex items-center gap-6 text-[11px] tracking-[0.22em] uppercase font-semibold text-sca-steel">
                        <span>Área · {p.area}</span>
                        <span className="capitalize">{PROJECT_CATEGORIES.find((c) => c.slug === p.category)?.label}</span>
                      </div>
                    </div>
                    <ArrowUpRight className="w-5 h-5 mt-1 text-sca-steel group-hover:text-sca-red group-hover:-translate-y-1 group-hover:translate-x-1 transition-all" />
                  </div>
                </article>
              </StaggerItem>
            ))}
          </Stagger>

          {projects.length === 0 && (
            <p className="text-center text-sca-steel py-20">Nenhum projeto encontrado para esta categoria.</p>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="bg-sca-mist py-24 md:py-32">
        <div className="sca-container grid grid-cols-1 md:grid-cols-12 gap-10 items-end">
          <Reveal className="md:col-span-8">
            <p className="sca-overline mb-6">[ Próximo projeto ]</p>
            <h2 className="font-sans text-3xl md:text-5xl font-bold tracking-tightest leading-[1.05]">
              Sua obra pode ser o próximo case <span className="sca-serif text-sca-red">técnico da SCA</span>.
            </h2>
          </Reveal>
          <Reveal delay={0.1} className="md:col-span-4 md:flex md:justify-end">
            <Link to="/contato" className="btn-sca-dark" data-testid="projects-cta-contact">
              Iniciar conversa técnica <ArrowUpRight className="w-4 h-4" />
            </Link>
          </Reveal>
        </div>
      </section>
    </>
  );
}
